import { useEffect } from 'react'
import { Alert, DatePicker, Form, Modal, Typography } from 'antd'
import type { Dayjs } from 'dayjs'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { createPeriod, type Period } from './financeApi'
import { submitOnValid } from '@/shared/lib/submitOnValid'

interface PeriodCreateModalProps {
  open: boolean
  /** Mavjud davrlar — takror kodni OLDINDAN aytish uchun */
  periods: readonly Period[]
  onClose: () => void
}

interface FormValues {
  month: Dayjs | null
}

/**
 * Yangi moliyaviy davr ochish.
 *
 * <h2>⚠️ OPERATOR FAQAT OYNI KO'RSATADI</h2>
 * Chegaralar SERVERDA hisoblanadi (`PeriodDraft#bounds`) va bu ataylab:
 * ular tenantning `day_timezone` iga bog'liq. `Asia/Kabul` = UTC+4:30,
 * ya'ni 2026-yil avgust `2026-07-31T19:30:00Z` da boshlanadi. Chegarani
 * klientga hisoblatish o'yinga UCHINCHI soatni — brauzerning zonasini —
 * qo'shardi va oy chegarasidagi tranzaksiyalar noto'g'ri davrga
 * tushardi. Xato hisobotda ko'rinmasdi.
 *
 * <h2>⚠️ KELAJAKDAGI OY TAQIQLANMAGAN</h2>
 * Davr OCHISH — zararsiz amal: uni bo'sh holda o'chirish mumkin. Odatiy
 * ish tartibi ham shuni talab qiladi: sentyabr xarajatini kiritish uchun
 * sentyabr davri OCHIQ bo'lishi kerak va u odatda oy boshlanishidan
 * oldin ochiladi.
 */
export function PeriodCreateModal({ open, periods, onClose }: PeriodCreateModalProps) {
  const { t } = useTranslation()
  const [form] = Form.useForm<FormValues>()
  const queryClient = useQueryClient()

  useEffect(() => {
    if (!open) return
    form.setFieldsValue({ month: null })
  }, [open, form])

  const create = useMutation({
    mutationFn: (values: FormValues) => createPeriod(values.month?.format('YYYY-MM') ?? ''),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.finance.periods })
      form.resetFields()
      onClose()
    },
  })

  const close = () => {
    create.reset()
    onClose()
  }

  return (
    <Modal
      open={open}
      title={t('finance.period.createTitle')}
      okText={t('finance.period.create')}
      cancelText={t('common.cancel')}
      confirmLoading={create.isPending}
      onCancel={close}
      onOk={() => submitOnValid(form, (values) => create.mutate(values))}
      destroyOnHidden
    >
      <Form<FormValues> form={form} layout="vertical" preserve={false} disabled={create.isPending}>
        {/* 409 ("bunday davr bor") SHU YERDA qoladi — oyna ochiq */}
        {create.isError ? (
          <Alert
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
            message={translateError(create.error, t)}
          />
        ) : null}

        <Form.Item
          name="month"
          label={t('finance.period.month')}
          extra={t('finance.period.monthHint')}
          rules={[
            { required: true, message: t('finance.period.monthRequired') },
            {
              validator: (_rule, value: Dayjs | null) => {
                if (!value) return Promise.resolve()
                const code = value.format('YYYY-MM')
                // ⚠️ Server ham 409 beradi, lekin operatorni bekor
                // urinishdan saqlaymiz (`StockMovementModal` dagi
                // "qoldiq yetmasligi" bilan bir xil qaror).
                return periods.some((period) => period.code === code)
                  ? Promise.reject(new Error(t('finance.period.monthTaken', { code })))
                  : Promise.resolve()
              },
            },
          ]}
        >
          <DatePicker picker="month" style={{ width: '100%' }} format="YYYY-MM" />
        </Form.Item>

        <Typography.Text type="secondary">{t('finance.period.createNote')}</Typography.Text>
      </Form>
    </Modal>
  )
}
